// src/components/TimelineModal.js
import React, { useEffect, useRef, useState } from "react";
import {
  loadInitialFeed,
  loadMoreFrom,
  canShowKebab,
  normalizeDate,
  getPostMetaMany,
  hasLiked,
  toggleLike,
  softDeletePost,
  softDeletePostByAuthor,
} from "../services/timelineService";
import nomesBonitosCategoria from "../utils/nomesBonitosCategoria";
import CommunityRulesModal from "./CommunityRulesModal";
import XPopup from "./XPopup";

/* ==== ADIÇÕES para Sugerir Candidato ==== */
import SugerirCandidatoModal from "./SugerirCandidatoModal";
import { getSugestaoConfig } from "../services/sugerirService";
import { db } from "../firebase";
/* ======================================== */

/* ===========================================
   Helpers de SPOILER inline ([spoiler]...[/spoiler])
   =========================================== */
function textHasInlineSpoiler(t = "") {
  return /\[spoiler\][\s\S]*?\[\/spoiler\]/i.test(String(t || ""));
}

function InlineSpoiler({ children }) {
  const [open, setOpen] = useState(false);
  return (
    <span
      className={`spoiler-inline ${open ? "revealed" : ""}`}
      role="button"
      tabIndex={0}
      onClick={() => setOpen((v) => !v)}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setOpen((v) => !v)}
      aria-label={open ? "Ocultar spoiler" : "Revelar spoiler"}
    >
      {children}
    </span>
  );
}

function renderTextWithSpoilers(text) {
  if (!text) return null;
  const lines = String(text).split("\n");
  return lines.map((line, i) => {
    const parts = [];
    let lastIndex = 0;
    const regex = /\[spoiler\]([\s\S]*?)\[\/spoiler\]/gi;
    let m;
    while ((m = regex.exec(line)) !== null) {
      const before = line.slice(lastIndex, m.index);
      if (before) parts.push(before);
      const inside = m[1] || "";
      parts.push(<InlineSpoiler key={`sp-${i}-${m.index}`}>{inside}</InlineSpoiler>);
      lastIndex = regex.lastIndex;
    }
    const tail = line.slice(lastIndex);
    if (tail) parts.push(tail);
    return <p key={i}>{parts.length ? parts : line}</p>;
  });
}

function SpoilerBlock({ text, defaultHidden = true }) {
  const [hidden, setHidden] = React.useState(!!defaultHidden);
  if (!text) return null;
  return (
    <div
      className={`spoiler ${hidden ? "" : "revealed"}`}
      onClick={() => hidden && setHidden(false)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && hidden && setHidden(false)}
    >
      {String(text).split("\n").map((line, i) => <p key={i}>{line}</p>)}
    </div>
  );
}

// helpers
function createdMs(p) {
  if (typeof p?.createdAtMs === "number") return p.createdAtMs;
  const ts = p?.createdAt;
  if (ts?.toMillis) return ts.toMillis();
  if (ts) return Number(new Date(ts)) || 0;
  return 0;
}
function sortPostsDesc(list) {
  return Array.isArray(list) ? [...list].sort((a, b) => createdMs(b) - createdMs(a)) : [];
}

const ICONS = {
  timeline: "https://i.imgur.com/zhvk1oW.png",
  reply: "https://i.imgur.com/SPqgsR9.png",
  starOff: "https://i.imgur.com/Q7VsevK.png",
  starOn: "https://i.imgur.com/BqPzEEV.png",
  arrowOverflow: "https://i.imgur.com/7B3fBYd.png",
};

function formatPtDate(dt) {
  if (!dt) return "";
  try {
    const d = normalizeDate(dt);
    const dia = d.toLocaleDateString("pt-PT", { day: "2-digit" });
    const mes = d.toLocaleDateString("pt-PT", { month: "long" });
    const hora = d.toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" });
    return `${dia} de ${mes}, às ${hora}`;
  } catch {
    return "";
  }
}
function prettyModo(m) {
  if (m === "oscar-indicados") return "Indicados ao Oscar";
  if (m === "oscar-vencedores") return "Vencedores do Oscar";
  if (m === "globodeouro-indicados") return "Indicados ao Globo de Ouro";
  if (m === "globodeouro-vencedores") return "Vencedores do Globo de Ouro";
  if (m === "sagawards-indicados") return "Indicados ao SAG Awards";
  if (m === "sagawards-vencedores") return "Vencedores do SAG Awards";
  if (m === "criticschoice-indicados") return "Indicados ao Critics Choice";
  if (m === "criticschoice-vencedores") return "Vencedores do Critics Choice";
  if (m === "dga-indicados") return "Indicados ao DGA Awards";
  if (m === "dga-vencedoress") return "Vencedores do DGA Awards";
  if (m === "pga-indicados") return "Indicados ao PGA Awards";
  if (m === "pga-vencedores") return "Vencedores do PGA Awards";
  if (m === "bafta-indicados") return "Indicados ao BAFTA";
  if (m === "bafta-vencedores") return "Vencedores do BAFTA";
  return m || "";
}
function prettyCategoria(c) {
  if (!c) return "";
  const key = String(c).toLowerCase().replace(/\s+/g, "");
  return nomesBonitosCategoria[key] || c;
}
function medalClassFor(pos, acertouRaw) {
  const errou = acertouRaw === false || acertouRaw === "false" || acertouRaw === 0;
  if (errou) return "cor-5err";
  if (pos === 1) return "cor-1";
  if (pos === 2) return "cor-2";
  if (pos === 3 || pos === 4) return "cor-3";
  return "cor-5";
}

/* ---------------------- PostCard ---------------------- */
function PostCard({ post, currentUid, onReplyClick, onEditClick, onDeleteClick, onToggleLike }) {
  const [liked, setLiked] = useState(!!post.likedByMe);
  useEffect(() => setLiked(!!post.likedByMe), [post.likedByMe]);

  const scrollerRef = useRef(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const check = () => setHasOverflow(el.scrollWidth > el.clientWidth + 2);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  function handleToggleLike() {
    const next = !liked;
    setLiked(next);
    onToggleLike?.(post.postId, next);
  }

  const goProfile = () => {
    if (!post.authorUid) return;
    window.location.href = `#/perfil-publico/${post.authorUid}`;
  };

  return (
    <article className="tl-card">
      <header className="tl-card-head">
        <button className="tl-avatar-wrap" onClick={goProfile} title="Ver perfil" type="button">
          {post.authorAvatar ? (
            <img className="tl-avatar" src={post.authorAvatar} alt={post.authorName || ""} />
          ) : (
            <div className="tl-avatar placeholder" aria-hidden />
          )}
        </button>

        <div className="tl-head-main">
          <div className="tl-name-row">
            {post.especialista && <span className="tl-badge">ESPECIALISTA OSCAR</span>}
            <button className="tl-author" onClick={goProfile} title="Ver perfil" type="button">
              {post.authorName}
            </button>
          </div>
        </div>
      </header>

      {post.text && (
        <div className="tl-textbox">
          {textHasInlineSpoiler(post.text) ? (
            renderTextWithSpoilers(post.text)
          ) : post.hasSpoiler ? (
            <SpoilerBlock text={post.text} />
          ) : (
            String(post.text).split("\n").map((line, i) => <p key={i}>{line}</p>)
          )}
        </div>
      )}

      {post.attach && (
        <section className="tl-attach">
          <div className="tl-attach-head">
            <div className="tl-attach-mode">Apostas – {prettyModo(post.attach.modo)}</div>
            <div className="tl-attach-cat">
              {(prettyCategoria(post.attach.categoria) || "").toUpperCase()}
            </div>
          </div>

          <div className="tl-carousel-wrap">
            <div className="tl-carousel" ref={scrollerRef}>
              {(post.attach.itens || []).map((item, idx) => {
                const pos = idx + 1;
                const errou =
                  item?.acertou === false || item?.acertou === "false" || item?.acertou === 0;
                const acertou =
                  item?.acertou === true || item?.acertou === "true" || item?.acertou === 1;

                return (
                  <div className="tl-item" key={`${item.titulo || "item"}-${idx}`}>
                    <div className={`tl-poster ${errou ? "desbotado" : ""}`}>
                      <img
                        src={item.imagem || "https://i.imgur.com/6MbQoCt.png"}
                        alt={item.titulo || ""}
                      />
                      {acertou && <span className="tl-hit-badge">ACERTEI</span>}
                      <div className={`tl-medal ${medalClassFor(pos, item?.acertou)}`}>
                        <span>{pos}º</span>
                      </div>
                    </div>

                    <div className="tl-item-info">
                      <strong className="tl-item-title">{item.titulo}</strong>
                      {item.filme && <div className="tl-item-sub">{item.filme}</div>}
                      {item.pais && !item.filme && <div className="tl-item-sub">{item.pais}</div>}
                    </div>
                  </div>
                );
              })}
            </div>

            {hasOverflow && (
              <div className="tl-overflow-hint" aria-hidden="true">
                <img src={ICONS.arrowOverflow} alt="" />
              </div>
            )}
          </div>
        </section>
      )}

      <div className="tl-date">{formatPtDate(post.createdAt || post.createdAtMs)}</div>

      <footer className="tl-card-foot">
        <div className="tl-foot-left">
          <button className="tl-foot-btn" onClick={() => onReplyClick?.(post)} type="button">
            <img src={ICONS.reply} alt="" />
            <span>{post.repliesCount ?? 0}</span>
            <span className="tl-foot-label">Reply</span>
          </button>

          <button className="tl-foot-btn" onClick={handleToggleLike} type="button">
            <img src={liked ? ICONS.starOn : ICONS.starOff} alt="" />
            <span>{post.likesCount ?? 0}</span>
            <span className="tl-foot-label">Merece a estrela</span>
          </button>
        </div>

        {canShowKebab({ currentUid, authorUid: post.authorUid }) && (
          <div className="tl-kebab" aria-label="Mais ações">
            <button
              className="tl-kebab-btn"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                document.querySelectorAll(".tl-kebab.open").forEach((k) => k.classList.remove("open"));
                const kebab = e.currentTarget.closest(".tl-kebab");
                if (kebab) kebab.classList.toggle("open");
              }}
            >
              ⋯
            </button>
            <div className="tl-kebab-menu">
              <button
                onClick={() => {
                  document.querySelectorAll(".tl-kebab.open").forEach((k) => k.classList.remove("open"));
                  try { sessionStorage.setItem("pm_editing", JSON.stringify(post)); } catch {}
                  onEditClick?.(post);
                }}
                type="button"
              >
                Editar
              </button>
              <button
                onClick={() => {
                  document.querySelectorAll(".tl-kebab.open").forEach((k) => k.classList.remove("open"));
                  onDeleteClick?.(post);
                }}
                type="button"
              >
                Apagar
              </button>
            </div>
          </div>
        )}
      </footer>
    </article>
  );
}

/* ---------------------- TimelineModal ---------------------- */
export default function TimelineModal({ currentUid, onClose, onOpenPost, onOpenReply }) {
  const [chunk, setChunk] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);

  // popup genérico existente
  const [popup, setPopup] = useState({
    open: false,
    tipo: "info",
    mensagem: "",
    onConfirm: null,
  });

  /* ==== ESTADOS para Sugerir Candidato ==== */
  const [sugerirOpen, setSugerirOpen] = useState(false);
  const [sugerirLoading, setSugerirLoading] = useState(false);
  const [showSugerirDisabled, setShowSugerirDisabled] = useState(false);
  /* ======================================== */

  useEffect(() => {
    const onDown = (e) => {
      if (e.target.closest?.(".tl-kebab")) return;
      document.querySelectorAll(".tl-kebab.open").forEach((k) => k.classList.remove("open"));
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, []);

  async function hydrateFromMeta(list) {
    const safe = Array.isArray(list) ? list : [];
    const ids = safe.map((p) => p.postId).filter(Boolean);
    if (!ids.length) return safe;

    const metas = await getPostMetaMany(ids);
    const likeFlags = await Promise.all(ids.map((id) => hasLiked({ id, postId: id, userUid: currentUid })));

    const byId = new Map(metas.map((m) => [m.postId, m]));
    const likedMap = new Map(ids.map((id, i) => [id, !!likeFlags[i]]));

    const mapped = safe.map((p) => {
      const meta = byId.get(p.postId);
      const overrideText = typeof meta?.text === "string" ? meta.text : null;
      const editedAtMs =
        meta?.editedAt?.toMillis
          ? meta.editedAt.toMillis()
          : meta?.editedAt
            ? Number(new Date(meta.editedAt)) || p.editedAtMs || null
            : p.editedAtMs ?? null;

      return {
        ...p,
        text: overrideText !== null ? overrideText : p.text,
        editedAtMs,
        hasSpoiler: typeof meta?.hasSpoiler === "boolean" ? meta.hasSpoiler : !!p.hasSpoiler,
        likesCount: meta?.likesCount ?? p.likesCount ?? 0,
        repliesCount: meta?.repliesCount ?? p.repliesCount ?? 0,
        likedByMe: likedMap.get(p.postId) ?? false,
        deletedByMeta: !!meta?.deleted,
        statusFromMeta: meta?.status || null,
      };
    });

    return mapped.filter((p) => !p.deletedByMeta && p.statusFromMeta !== "deleted");
  }

  function saveCache(c, p) {
    try {
      sessionStorage.setItem("tl_cache_v1", JSON.stringify({ chunk: c || null, posts: Array.isArray(p) ? p : [] }));
    } catch {}
  }

  useEffect(() => {
    (async () => {
      const cache = sessionStorage.getItem("tl_cache_v1");
      if (cache) {
        try {
          const parsed = JSON.parse(cache);
          const cCached = parsed?.chunk || null;
          const pCached = Array.isArray(parsed?.posts) ? parsed.posts : [];
          const pDesc = sortPostsDesc(pCached);
          const pHydr = await hydrateFromMeta(pDesc);
          setChunk(cCached);
          setPosts(pHydr);
          setHasMore(!!cCached && cCached.index > 0);
          setLoading(false);
        } catch {}
      }

      const fresh = await loadInitialFeed();
      const c = fresh?.chunk || null;
      const p = Array.isArray(fresh?.posts) ? fresh.posts : [];
      const pDesc = sortPostsDesc(p);
      const pHydr = await hydrateFromMeta(pDesc);
      setChunk(c);
      setPosts(pHydr);
      setHasMore(!!c && c.index > 0);
      setLoading(false);
      saveCache(c, pHydr);
    })();

    const onReplyCount = (ev) => {
      const { postId, count } = ev.detail || {};
      if (!postId) return;
      setPosts((list) => {
        const safe = Array.isArray(list) ? list : [];
        const next = safe.map((p) => (p.postId === postId ? { ...p, repliesCount: count ?? 0 } : p));
        saveCache(chunk, next);
        return next;
      });
    };
    window.addEventListener("tl:reply-count", onReplyCount);

    const onPostDone = (ev) => {
      const payload = ev.detail || {};
      if (payload?.type === "create" && payload.postLite) {
        setPosts((cur) => {
          const next = [payload.postLite, ...(Array.isArray(cur) ? cur : [])];
          saveCache(chunk, next);
          return next;
        });
      } else if (payload?.type === "edit" && payload.postId) {
        setPosts((cur) => {
          const next = (Array.isArray(cur) ? cur : []).map((p) =>
            p.postId === payload.postId
              ? {
                  ...p,
                  text: payload.newText ?? payload.text ?? p.text,
                  attach: payload.attach ?? p.attach,
                  editedAtMs: Date.now(),
                  hasSpoiler:
                    typeof payload.hasSpoiler === "boolean" ? payload.hasSpoiler : p.hasSpoiler,
                }
              : p
          );
          saveCache(chunk, next);
          return next;
        });
      }
    };
    window.addEventListener("tl:post-done", onPostDone);

    return () => {
      window.removeEventListener("tl:reply-count", onReplyCount);
      window.removeEventListener("tl:post-done", onPostDone);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUid]);

  async function handleLoadMore() {
    if (!chunk || loadingMore) return;
    setLoadingMore(true);
    const res = await loadMoreFrom(chunk);
    setLoadingMore(false);

    const older = res?.chunk || null;
    const more = Array.isArray(res?.posts) ? res.posts : [];
    if (!older || more.length === 0) {
      setHasMore(false);
      return;
    }

    setChunk(older);
    const moreDesc = sortPostsDesc(more);
    const moreHydr = await hydrateFromMeta(moreDesc);
    setPosts((cur) => {
      const safe = Array.isArray(cur) ? cur : [];
      const next = [...safe, ...moreHydr];
      saveCache(older, next);
      return next;
    });
    setHasMore(older.index > 0);
  }

  async function handleToggleLike(postId, likeWanted) {
    setPosts((list) => {
      const safe = Array.isArray(list) ? list : [];
      const next = safe.map((p) => {
        if (p.postId !== postId) return p;
        const already = !!p.likedByMe;
        const delta = likeWanted === already ? 0 : likeWanted ? 1 : -1;
        return { ...p, likedByMe: likeWanted, likesCount: Math.max(0, (p.likesCount ?? 0) + delta) };
      });
      saveCache(chunk, next);
      return next;
    });

    try {
      await toggleLike({ postId, userUid: currentUid, like: likeWanted });
      const metas = await getPostMetaMany([postId]);
      const meta = metas?.[0];
      const liked = await hasLiked({ postId, userUid: currentUid });

      setPosts((list) => {
        const safe = Array.isArray(list) ? list : [];
        const next = safe.map((p) =>
          p.postId === postId
            ? {
                ...p,
                likesCount: meta?.likesCount ?? p.likesCount ?? 0,
                repliesCount: meta?.repliesCount ?? p.repliesCount ?? 0,
                likedByMe: !!liked,
              }
            : p
        );
        saveCache(chunk, next);
        return next;
      });
    } catch (e) {
      setPosts((list) => {
        const safe = Array.isArray(list) ? list : [];
        const next = safe.map((p) => {
          if (p.postId !== postId) return p;
          const delta = likeWanted ? -1 : 1;
          return { ...p, likedByMe: !likeWanted, likesCount: Math.max(0, (p.likesCount ?? 0) + delta) };
        });
        saveCache(chunk, next);
        return next;
      });
      setPopup({
        open: true,
        tipo: "info",
        mensagem: "Não foi possível atualizar o like agora.",
        onConfirm: null,
      });
    }
  }

  async function handleDeletePost(post) {
    if (!post?.postId) return;

    setPopup({
      open: true,
      tipo: "confirm",
      mensagem: "Apagar este post? (Você pode remover seu próprio post)",
      onConfirm: async () => {
        setPopup({ open: false, tipo: "info", mensagem: "", onConfirm: null });
        try {
          if (currentUid && currentUid === post.authorUid) {
            await softDeletePostByAuthor(post.postId);
          } else {
            await softDeletePost(post.postId);
          }
          setPosts((cur) => {
            const safe = Array.isArray(cur) ? cur : [];
            const next = safe.filter((p) => p.postId !== post.postId);
            saveCache(chunk, next);
            return next;
          });
        } catch (e) {
          console.error(e);
          setPopup({
            open: true,
            tipo: "info",
            mensagem:
              "Não foi possível apagar. Verifique se suas permissões permitem essa ação.",
            onConfirm: null,
          });
        }
      },
    });
  }

  function openComposerNew() {
    try {
      sessionStorage.removeItem("pm_editing");
    } catch {}
    onOpenPost?.({
      editing: null,
      editPost: null,
      onDone: (payload) => {
        if (payload?.type === "create" && payload.postLite) {
          setPosts((cur) => {
            const next = [payload.postLite, ...(Array.isArray(cur) ? cur : [])];
            saveCache(chunk, next);
            return next;
          });
        } else if (payload?.type === "edit" && payload.postId) {
          setPosts((cur) => {
            const next = (Array.isArray(cur) ? cur : []).map((p) =>
              p.postId === payload.postId
                ? {
                    ...p,
                    text: payload.newText ?? payload.text ?? p.text,
                    attach: payload.attach ?? p.attach,
                    editedAtMs: Date.now(),
                    hasSpoiler:
                      typeof payload.hasSpoiler === "boolean"
                        ? payload.hasSpoiler
                        : p.hasSpoiler,
                  }
                : p
            );
            saveCache(chunk, next);
            return next;
          });
        }
      },
    });
  }

  function openComposerEdit(p) {
    try {
      sessionStorage.setItem("pm_editing", JSON.stringify(p));
    } catch {}
    onOpenPost?.({
      editing: p,
      editPost: p,
      defaultText: p.text,
      onDone: (payload) => {
        if (payload?.type === "edit" && payload.postId) {
          setPosts((cur) => {
            const next = (Array.isArray(cur) ? cur : []).map((it) =>
              it.postId === payload.postId
                ? {
                    ...it,
                    text: payload.newText ?? payload.text ?? it.text,
                    attach: payload.attach ?? it.attach,
                    editedAtMs: Date.now(),
                    hasSpoiler:
                      typeof payload.hasSpoiler === "boolean"
                        ? payload.hasSpoiler
                        : it.hasSpoiler,
                  }
                : it
            );
            saveCache(chunk, next);
            return next;
          });
        }
      },
    });
  }

  /* ==== Handler do botão SUGERIR CANDIDATO ==== */
  async function handleAbrirSugerir() {
  if (sugerirLoading) return;
  setSugerirLoading(true);
  try {
    const cfg = await getSugestaoConfig(db); // 1 leitura só no clique
    console.log("[Sugerir] config lida:", cfg);  // << log pra debug

    if (cfg && cfg.enabled === true) {
      setSugerirOpen(true);
    } else if (cfg && cfg.enabled === false) {
      // desligado explicitamente → popup azul
      setShowSugerirDisabled(true);
    } else {
      // fallback: se algo estranho vier, abre o modal (ele mesmo mostrará 'desativado')
      setSugerirOpen(true);
    }
  } catch (e) {
    console.error("[Sugerir] erro ao ler config:", e);
    // fallback de debug: abre o modal para você ver a UI e a mensagem interna
    setSugerirOpen(true);
  } finally {
    setSugerirLoading(false);
  }
}
  /* ============================================ */

  return (
    <div className="tl-modal">
      <button className="tl-backdrop" onClick={onClose} aria-label="Fechar timeline" />
      <div className="tl-modal-inner">
        <header className="tl-head">
          <div className="tl-head-title">
            <h2>TIMELINE TERMOMETEIRO</h2>
          </div>

          {/* ====== Linha de botões centralizada (Opção B) ====== */}
          <div
            style={{
              display: "flex",
              gap: 0,
              justifyContent: "center",
              alignItems: "center",
              marginTop: 10,
            }}
          >
            <button
              type="button"
              className="tl-post-btn2"
              onClick={() => setRulesOpen(true)}
              title="Ver regras da comunidade"
            >
              <span>REGRAS DA COMUNIDADE</span>
            </button>

            <button
              type="button"
              className={`btn-sugerir-candidato ${sugerirLoading ? "is-loading" : ""}`}
              onClick={handleAbrirSugerir}
              title="Sugerir candidato"
              disabled={sugerirLoading}
            >
              {sugerirLoading ? "Abrindo…" : "SUGERIR CANDIDATO"}
            </button>
          </div>
          {/* ==================================================== */}
        </header>

        <div className="tl-feed">
          {loading ? (
            <div className="tl-loading">Carregando…</div>
          ) : !Array.isArray(posts) || posts.length === 0 ? (
            <div className="tl-empty">Sem posts ainda. Seja o primeiro a postar!</div>
          ) : (
            posts.map((post) => (
              <PostCard
                key={post.postId}
                post={post}
                currentUid={currentUid}
                onReplyClick={(p) => onOpenReply?.(p)}
                onEditClick={(p) => openComposerEdit(p)}
                onDeleteClick={handleDeletePost}
                onToggleLike={handleToggleLike}
              />
            ))
          )}

          {!loading && Array.isArray(posts) && posts.length > 0 && hasMore && (
            <div className="tl-more">
              <div className="tl-more-gradient" />
              <button className="tl-more-btn" onClick={handleLoadMore} disabled={loadingMore}>
                {loadingMore ? "Carregando…" : "LER MAIS POSTS"}
              </button>
            </div>
          )}
        </div>

        <div className="tl-bottom-bar">
          <button className="tl-post-btn" onClick={openComposerNew}>
            <span> POSTAR NA TIMELINE</span>
          </button>
          <button className="tl-close-btn" onClick={onClose}>
            FECHAR TIMELINE
          </button>
        </div>
      </div>

      {/* Regras */}
      <CommunityRulesModal open={rulesOpen} onClose={() => setRulesOpen(false)} />

      {/* Popup global do TimelineModal (já existente) */}
      <XPopup
        open={popup.open}
        tipo={popup.tipo}
        mensagem={popup.mensagem}
        onClose={() => setPopup({ open: false, tipo: "info", mensagem: "", onConfirm: null })}
        onConfirm={popup.onConfirm}
      />

      {/* Popup: Função desativada no momento (usa prop 'mensagem' do XPopup) */}
      <XPopup
        open={showSugerirDisabled}
        tipo="info"
        mensagem={
          'Função desativada no momento.\nA opção "Sugerir Candidato" não está disponível agora.'
        }
        onClose={() => setShowSugerirDisabled(false)}
      />

      {/* Modal principal: Sugerir Candidato */}
      <SugerirCandidatoModal
        isOpen={sugerirOpen}
        onClose={() => setSugerirOpen(false)}
        currentUid={currentUid}
      />
    </div>
  );
}
